import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api } from './api'

/** What the queue can be in, and worth counting separately. */
const STATUSES = ['Published', 'Scheduled', 'Approved', 'Draft', 'Failed']

/**
 * What the platform knows about a business's own output.
 *
 * Reach, likes and comments come from Meta and cannot be shown before a page is connected and
 * something has actually been published — so this counts what we do know rather than drawing empty
 * charts. It says plainly which half is missing and why.
 */
export default function Analytics({ session }) {
  const { t, i18n } = useTranslation()
  const [counts, setCounts] = useState(null)
  const [published, setPublished] = useState([])
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        const results = await Promise.all(STATUSES.map((status) => api.posts(status)))
        if (cancelled) return

        setCounts(Object.fromEntries(STATUSES.map((status, index) => [status, results[index].length])))
        setPublished(results[STATUSES.indexOf('Published')].slice(0, 10))
        setError(null)
      } catch (failure) {
        if (!cancelled) setError(failure.message)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [])

  const total = counts ? Object.values(counts).reduce((sum, value) => sum + value, 0) : 0

  return (
    <>
      <section className="card">
        <h2>{t('analytics.title')}</h2>
        <p className="muted small">{t('analytics.help')}</p>

        {error && <p className="status status--bad">{error}</p>}

        <div className="stats">
          {STATUSES.map((status) => (
            <div key={status} className="stat">
              <div className="stat__value">{counts ? counts[status] : '—'}</div>
              <div className="stat__label">{t(`posts.status.${status}`)}</div>
            </div>
          ))}

          <div className="stat">
            <div className="stat__value">{session.credits}</div>
            <div className="stat__label">{t('analytics.credits')}</div>
          </div>
        </div>

        {total === 0 && <p className="muted">{t('analytics.nothingYet')}</p>}
      </section>

      <section className="card">
        <h2>{t('analytics.publishedTitle')}</h2>

        {published.length === 0 ? (
          <p className="muted">{t('analytics.nonePublished')}</p>
        ) : (
          published.map((post) => (
            <div key={post.id} className="postrow postrow--static">
              {post.variants[0]?.imageUrl ? (
                <img className="postrow__thumb" src={post.variants[0].imageUrl} alt="" loading="lazy" />
              ) : (
                <span className="postrow__thumb postrow__thumb--empty">🖼</span>
              )}

              <span className="postrow__body">
                <span className="postrow__title">{post.products?.[0]?.name ?? `#${post.id}`}</span>
                <span className="postrow__text">{post.variants[0]?.text}</span>
              </span>

              <span className="postrow__meta">
                {post.publishedAt && (
                  <span className="muted small">
                    {new Date(post.publishedAt).toLocaleDateString(i18n.resolvedLanguage)}
                  </span>
                )}
              </span>
            </div>
          ))
        )}
      </section>

      <section className="card">
        <h2>{t('analytics.reachTitle')}</h2>
        {/* Said rather than mocked up. An empty chart labelled "reach" implies the number exists
            and is zero; the truth is that nobody has asked Facebook yet. */}
        <p className="muted">{t('analytics.reachPending')}</p>
      </section>
    </>
  )
}
